import clsx from 'clsx';
import JSON5 from 'json5';
import { useMemo } from 'react';

import { CollapsibleSources } from '../renderers/CollapsibleSources';
import { getRenderer } from '../renderers/registry';
import { ColumnAnalysis, CellValue } from '../types';

import styles from './RecordsView.module.scss';

interface RecordsViewProps {
  headers: string[];
  rows: Record<string, CellValue>[];
  rowNumbers: number[];
  analyses: ColumnAnalysis[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}

const asText = (value: CellValue): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const FILENAME_RE = /\.\w{1,5}$/;

// Does a cell value look like a filename or list of filenames?
const looksLikeFilenames = (value: CellValue): boolean => {
  if (value === null || value === undefined || value === '') return false;
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) return false;

  // Single filename
  if (FILENAME_RE.test(text) && !text.includes('\n')) return true;

  // Array of filenames (JSON or Python-style single-quoted)
  if (text.startsWith('[')) {
    try {
      const parsed = JSON5.parse(text);
      if (Array.isArray(parsed)) return parsed.every((s: unknown) => typeof s === 'string' && FILENAME_RE.test(s));
    } catch {
      // not parseable
    }
  }

  return false;
};

export const RecordsView = ({ headers, rows, rowNumbers, analyses, selectedIndex, onSelect }: RecordsViewProps) => {
  const kindByColumn = new Map(analyses.map(a => [a.id, a.kind]));
  const labelColumn =
    analyses.find(a => (a.kind === 'plain' || a.kind === 'longtext') && a.medianLength >= 10 && a.medianLength < 80)
      ?.id ??
    analyses.find(a => a.kind === 'plain' || a.kind === 'longtext')?.id ??
    headers[0];

  // Detect filename→content column pairs by inspecting values.
  // A "filenames" column has values that look like filenames or arrays of filenames.
  // Its paired content column is the one immediately after it in the header order.
  const { collapsibleBase, hiddenColumns } = useMemo(() => {
    const base = new Map<string, string>(); // content column → filenames column
    const hidden = new Set<string>();
    if (rows.length === 0) return { collapsibleBase: base, hiddenColumns: hidden };

    const sampleRows = rows.slice(0, 10);

    headers.forEach((header, idx) => {
      if (idx >= headers.length - 1) return;
      const allFilenames = sampleRows.every(row => {
        const v = row[header];
        return v === null || v === undefined || v === '' || looksLikeFilenames(v);
      });
      const hasAnyFilename = sampleRows.some(row => looksLikeFilenames(row[header]));
      if (!allFilenames || !hasAnyFilename) return;

      const nextHeader = headers[idx + 1];
      const hasLongContent = sampleRows.some(row => {
        const v = row[nextHeader];
        return typeof v === 'string' && v.length > 100;
      });
      if (hasLongContent) {
        base.set(nextHeader, header);
        hidden.add(header);
      }
    });

    return { collapsibleBase: base, hiddenColumns: hidden };
  }, [headers, rows]);

  const selected = rows[selectedIndex];

  return (
    <div className={styles.records}>
      <div className={styles.list}>
        <div className={styles.listHead}>{rows.length} records</div>
        {rows.map((row, index) => (
          <button
            type="button"
            key={rowNumbers[index]}
            className={clsx(styles.item, index === selectedIndex && styles.itemOn)}
            onClick={() => onSelect(index)}
          >
            <div className={styles.itemNum}>{rowNumbers[index]}</div>
            <div className={styles.itemLabel}>{asText(row[labelColumn]) || '—'}</div>
          </button>
        ))}
      </div>
      <div className={styles.pane}>
        {selected &&
          headers.map(header => {
            if (hiddenColumns.has(header)) return null;

            const filesCol = collapsibleBase.get(header);
            if (filesCol) {
              return (
                <div
                  key={header}
                  className={styles.field}
                >
                  <div className={styles.fieldHead}>{header}</div>
                  <div className={styles.fieldValue}>
                    <CollapsibleSources
                      filenames={selected[filesCol]}
                      contents={selected[header]}
                    />
                  </div>
                </div>
              );
            }

            const renderer = getRenderer(kindByColumn.get(header) ?? 'plain');
            const { Full } = renderer;
            return (
              <div
                key={header}
                className={styles.field}
              >
                <div className={styles.fieldHead}>{header}</div>
                <div className={styles.fieldValue}>
                  <Full value={selected[header]} />
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};
